import { TanStackDevtools } from '@tanstack/react-devtools'
import { createRootRoute, Outlet, useMatches } from '@tanstack/react-router'
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools'
import { HashSet } from 'effect'
import type { ReactElement } from 'react'

import { DefaultProvider } from '../layouts/base/DefaultProvider.tsx'
import { MemberLayoutBase } from '../layouts/base/MemberLayoutBase.tsx'
import { isMaintainancePeriod, MaintainanceLayout } from '../layouts/MaintainanceLayout.tsx'
import { NoMemberLayout } from '../layouts/NoMemberLayout.tsx'
import { ErrorProvider } from '../providers/ErrorProvider.tsx'
import type { FileRoutesById } from '../routeTree.gen.ts'

const NO_MEMBER_LAYOUT_PATHS = HashSet.fromIterable<keyof FileRoutesById>([
  '/',
  '/auth/$provider/callback',
  '/callback/integration',
  '/clear_cache',
  '/error',
  '/o/$organizationId/sign_in',
  '/sign_in',
  '/sign_out',
  '/sign_up',
])

const Wrap = ({ children }: { children: ReactElement }): ReactElement => {
  const matches = useMatches()
  if (isMaintainancePeriod()) {
    return <MaintainanceLayout />
  }

  if (HashSet.has(NO_MEMBER_LAYOUT_PATHS, matches.at(-1)?.fullPath)) {
    return (
      <DefaultProvider>
        <NoMemberLayout>{children}</NoMemberLayout>
      </DefaultProvider>
    )
  }

  return (
    <DefaultProvider>
      <ErrorProvider>
        <MemberLayoutBase>{children}</MemberLayoutBase>
      </ErrorProvider>
    </DefaultProvider>
  )
}

export const Route = createRootRoute({
  component: () => {
    const { MODE } = import.meta.env
    return (
      <>
        <Wrap>
          <Outlet />
        </Wrap>
        {MODE === 'development' && (
          <TanStackDevtools
            config={{
              position: 'bottom-right',
            }}
            plugins={[
              {
                name: 'Tanstack Router',
                render: <TanStackRouterDevtoolsPanel />,
              },
            ]}
          />
        )}
      </>
    )
  },
})
